import { IconButtonStyledToggle } from "@components/collapsibleSection/CollapsibleSection.styles";
import { CaretIcon } from "@components/icon";
import { getInfoValue, IFieldDef, IGetValueArgs, isVisible } from "@components/smart/FieldInfo";
import {
    ISmartFieldBlur,
    ISmartFieldChange,
    ISmartFieldTempDataActionArgs
} from "@components/smart/smartField/SmartField";
import { getTooltip } from "@components/smart/smartField/SmartFieldUtils";
import SmartListField from "@components/smart/smartField/SmartListField";
import { IActionItem } from "@components/smart/smartFormGroup/SmartActionButton";
import { IFormGroupDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import BindingContext, { createPath, IEntity, splitPath } from "@odata/BindingContext";
import { ICachedFieldState } from "@odata/Data.utils";
import { withOData } from "@odata/withOData";
import { composeRefHandlers, forEachKey, getValue } from "@utils/general";
import { memoizeOne } from "@utils/memoizeOne";
import { normalizeProp } from "@utils/zoom.utils";
import i18next from "i18next";
import React from "react";
import { withTranslation } from "react-i18next";
import AutoSizer from "react-virtualized-auto-sizer";
import {
    ListChildComponentProps,
    ListOnItemsRenderedProps,
    ListOnScrollProps,
    VariableSizeList,
    VariableSizeListProps
} from "react-window";

import { withPermissionContext } from "../../contexts/permissionContext/withPermissionContext";
import { FormMode, IconSize } from "../../enums";
import { ModelEvent } from "../../model/Model";
import TestIds from "../../testIds";
import { VIEW_PADDING_VALUE } from "../View.styles";
import { FormForExtend, FormTranslationFiles } from "./Form";
import { getGroupCollectionTitle } from "./Form.utils";
import { FormAlertPosition } from "./FormStorage";
import { FormContent, FormFooter, ViewStyled } from "./FormView.styles";
import {
    FixedSearchWrapper,
    GroupHeaderStyled,
    GroupHeaderWrapper,
    VariableSizeListStyled
} from "./VirtualizedForm.styles";
import {
    CUSTOMIZE_FIELDS_ACTION,
    getAddableFields,
    isFieldCurrentlyVisible,
    isFieldMatchingSearchTerm,
    setFieldVisibility,
    setMassFieldsVisibility,
    shouldFieldBeVisible,
    TSmartVirtualizedFormStorage
} from "./VirtualizedForm.utils";
import VirtualizedFormGroupFooter from "./VirtualizedFormGroupFooter";
import VirtualizedFormLineItemsAdd from "./VirtualizedFormLineItemsAdd";
import VirtualizedFormSearch from "./VirtualizedFormSearch";

enum Type {
    Header,
    Group,
    GroupAddLineItem,
    GroupFooter,
    Field,
    Custom
}

interface IFlattenedRenderDef {
    id: string;
    type: Type;
    origId?: string;
    bc?: BindingContext;
    collectionBc?: BindingContext;
    groupDef?: IFormGroupDef;
    isSearchMatch?: boolean;
}

interface IListItemData {
    flattened: IFlattenedRenderDef[];
    search: string;
    activeSearchRowId: string | null;
    searchMatchedRowIds: string[];
}

type TVirtualizedListProps = VariableSizeListProps<IListItemData>;
const VariableSizeListStyledTyped =
    VariableSizeListStyled as unknown as React.ComponentClass<TVirtualizedListProps>;

interface IStructuredVariantGroupDef {
    groups: IFormGroupDef[];
    collections: Record<string, IStructuredVariantGroupDef>
}

const defaultHeightByType: Record<Type, number> = {
    [Type.Header]: 59 + 38,
    [Type.Group]: 31 + 2 * 13,
    [Type.GroupAddLineItem]: 38 + 3,
    [Type.GroupFooter]: 38 + 3,
    [Type.Field]: 43,
    [Type.Custom]: 48
};

const ROW_HEIGHT_CHANGE_TOLERANCE = 1;

interface ICollapsedInfo {
    isCollapsed: boolean;
    isParentCollapsed?: boolean; // if collapsed, shares info if collapsed because of parent group, or because of itself
}

class VirtualizedForm extends FormForExtend {
    _virtualizedListRef = React.createRef<VariableSizeList>();
    _rowElements: Record<string, HTMLDivElement> = {};
    _rowResizeObservers: Record<string, ResizeObserver> = {};
    _flattenedRenderDef: IFlattenedRenderDef[] = [];
    _collapsedGroups: Record<string, boolean> = {};
    _rowHeights: Record<string, number> = {};
    _search = "";
    _measureAnimationFrame: number | null = null;
    _pendingMeasureRange: { startIndex: number, stopIndex: number } | null = null;
    _searchVisibilityAnimationFrame: number | null = null;
    _searchVisibilityObserver: IntersectionObserver | null = null;
    _virtualizedSearchElement: HTMLDivElement | null = null;
    _listOuterElement: HTMLDivElement | null = null;
    _listScrollOffset = 0;
    _showFixedSearch = false;
    _searchMatchedRowIds: string[] = [];
    _activeSearchRowId: string | null = null;

    get storage(): TSmartVirtualizedFormStorage {
        return this.props.storage as TSmartVirtualizedFormStorage;
    }

    get isAuditTrail(): boolean {
        return this.storage.formMode === FormMode.AuditTrail;
    }

    componentDidMount() {
        super.componentDidMount();
        this.props.storage.emitter.on(ModelEvent.AfterLoad, this.handleAfterLoad);
        // todo: init visible field on init and on each AfterLoad or refactor visibility, so it don't need to be initialized;
        this.scheduleDomRecalculate();
        this.syncRenderedFieldVisibility();
    }

    componentDidUpdate(prevProps: Readonly<typeof this.props>): void {
        super.componentDidUpdate(prevProps);
        this.scheduleDomRecalculate();
        this.syncRenderedFieldVisibility();
    }

    componentWillUnmount() {
        super.componentWillUnmount();
        this.props.storage.emitter.off(ModelEvent.AfterLoad, this.handleAfterLoad);

        if (this._measureAnimationFrame !== null) {
            cancelAnimationFrame(this._measureAnimationFrame);
        }

        if (this._searchVisibilityAnimationFrame !== null) {
            cancelAnimationFrame(this._searchVisibilityAnimationFrame);
        }

        Object.values(this._rowResizeObservers).forEach(observer => observer.disconnect());
        this._rowResizeObservers = {};
        this.disposeSearchVisibilityObserver();
        this._virtualizedSearchElement = null;
        this._listOuterElement = null;
        this._pendingMeasureRange = null;
    }

    getGroupIdFromBc(bc: BindingContext): string {
        return this.storage._getGroupKeyFromBc(bc);
    }

    handleAfterLoad = () => {
        this.scheduleDomRecalculate();
        this.syncRenderedFieldVisibility();
    }

    refresh(recalculate?: boolean) {
        if (recalculate) {
            this.scheduleDomRecalculate();
        }
        this.storage.refreshFields(true);
        this.forceUpdate();
    }

    getFieldDefinition(bc: BindingContext): IFieldDef {
        const info = this.storage.getInfo(bc);

        if (!info) {
            const path = bc.getNavigationPath(true);
            console.warn(`Field definition not found for path: ${path}`);
        }

        return info;
    }

    refreshIfAffectsAnother(bc: BindingContext): void {
        const info = this.storage.getInfo(bc);
        if (info?.affectedFields) {
            // if field affects other fields, we need to refresh the form to recalculate visibility and other properties
            this.refresh(true);
        }
    }

    // basic handlers for field events
    handleBlur = async (args: ISmartFieldBlur): Promise<void> => {
        args.groupId = this.getGroupIdFromBc(args.bindingContext);
        this.refreshIfAffectsAnother(args.bindingContext);
        this.props.onBlur?.(args);
    };

    handleChange = (e: ISmartFieldChange): void => {
        e.groupId = this.getGroupIdFromBc(e.bindingContext);
        if (e.triggerAdditionalTasks) {
            this.refreshIfAffectsAnother(e.bindingContext);
        }
        this.props.onChange(e);
    };

    handleTemporalChange = (e: ISmartFieldChange): void => {
        e.groupId = this.getGroupIdFromBc(e.bindingContext);
        this.props.onTemporalChange?.(e);
    };

    handleCancel = (args: ISmartFieldTempDataActionArgs): void => {
        this.props.onCancel?.({
            bindingContext: args.bindingContext,
            groupId: this.getGroupIdFromBc(args.bindingContext)
        });
    };

    handleFieldStateChange = (bc: BindingContext, state: ICachedFieldState, prevState: ICachedFieldState): void => {
        if (state.isVisible !== prevState.isVisible) {
            this.forceUpdate();
        }
        this.props.onFieldStateChange?.(bc, state, prevState);
    };

    handleConfirm = (args: ISmartFieldTempDataActionArgs): void => {
        this.props.onConfirm?.({
            bindingContext: args.bindingContext,
            groupId: this.getGroupIdFromBc(args.bindingContext)
        });
    };

    // Public API for follow-up steps where rows are measured/updated from outside.
    recalculateVirtualizedList(): void {
        this.resetVirtualizedListFromIndex(0);
        this.scheduleDomRecalculate();
    }

    recalculateVirtualizedListFromDom(): void {
        this.scheduleDomRecalculate();
    }

    syncRenderedFieldVisibility = (): void => {
        let hasChanges = false;

        this._flattenedRenderDef.forEach(item => {
            if (item.type !== Type.Field || !item.bc || item.collectionBc) {
                return;
            }

            const info = this.storage.getInfo(item.bc);
            if (!info) {
                return;
            }

            const visibilityValue = isVisible({ storage: this.storage, context: this.storage.context, bindingContext: item.bc, info });

            if (visibilityValue === false) {
                hasChanges = setFieldVisibility(this.storage, item.bc, false) || hasChanges;
                return;
            }

            const shouldBeVisible = shouldFieldBeVisible(this.storage, item.bc);

            if (shouldBeVisible) {
                // we don't auto hide the field, only show it automatically.
                hasChanges = setFieldVisibility(this.storage, item.bc, shouldBeVisible) || hasChanges;
            }
        });

        if (hasChanges) {
            this.forceUpdate();
        }
    };

    updateVirtualizedRowHeight(id: string, height: number): void {
        if (!id || !height) {
            return;
        }

        if (!this.shouldUpdateRowHeight(id, height)) {
            return;
        }

        this._rowHeights[id] = height;
        const rowIndex = this._flattenedRenderDef.findIndex(item => item.id === id);
        if (rowIndex >= 0) {
            this.resetVirtualizedListFromIndex(rowIndex);
        }
    }

    resetVirtualizedListFromIndex = (index = 0): void => {
        this._virtualizedListRef.current?.resetAfterIndex(Math.max(index, 0), true);
    };

    scheduleDomRecalculate = (): void => {
        this._pendingMeasureRange = null;
        this.scheduleHeightRecalculate();
    };

    scheduleVisibleRangeRecalculate = (startIndex: number, stopIndex: number): void => {
        if (startIndex < 0 || stopIndex < startIndex) {
            return;
        }

        if (this._pendingMeasureRange) {
            this._pendingMeasureRange = {
                startIndex: Math.min(this._pendingMeasureRange.startIndex, startIndex),
                stopIndex: Math.max(this._pendingMeasureRange.stopIndex, stopIndex)
            };
        } else {
            this._pendingMeasureRange = { startIndex, stopIndex };
        }

        this.scheduleHeightRecalculate();
    };

    scheduleHeightRecalculate = (): void => {
        if (this._measureAnimationFrame !== null) {
            cancelAnimationFrame(this._measureAnimationFrame);
        }

        this._measureAnimationFrame = requestAnimationFrame(() => {
            const range = this._pendingMeasureRange;
            this._pendingMeasureRange = null;

            if (range) {
                this.recalculateHeightsFromRenderedRange(range.startIndex, range.stopIndex);
                return;
            }

            this.recalculateHeightsFromDom();
        });
    };

    measureRowHeight = (element: HTMLDivElement): number => {
        return Math.round(normalizeProp(element.getBoundingClientRect().height));
    };

    shouldUpdateRowHeight = (id: string, height: number): boolean => {
        if (!height) {
            return false;
        }

        const previousHeight = this._rowHeights[id];
        if (!previousHeight) {
            return true;
        }

        return Math.abs(previousHeight - height) > ROW_HEIGHT_CHANGE_TOLERANCE;
    };

    recalculateHeightsFromDom = (): void => {
        const visibleRowIds = new Set(this._flattenedRenderDef.map(item => item.id));
        let firstChangedIndex = Number.MAX_SAFE_INTEGER;

        let hasChanged = false;

        Object.keys(this._rowHeights).forEach(id => {
            if (!visibleRowIds.has(id)) {
                delete this._rowHeights[id];
                hasChanged = true;
            }
        });

        this._flattenedRenderDef.forEach((item, index) => {
            const element = this._rowElements[item.id];
            if (!element) {
                return;
            }

            const height = this.measureRowHeight(element);
            if (!this.shouldUpdateRowHeight(item.id, height)) {
                return;
            }

            this._rowHeights[item.id] = height;
            hasChanged = true;
            firstChangedIndex = Math.min(firstChangedIndex, index);
        });

        if (hasChanged && firstChangedIndex !== Number.MAX_SAFE_INTEGER) {
            this.resetVirtualizedListFromIndex(firstChangedIndex);
        }
    };

    recalculateHeightsFromRenderedRange = (startIndex: number, stopIndex: number): void => {
        let firstChangedIndex = Number.MAX_SAFE_INTEGER;
        let hasChanged = false;

        const safeStart = Math.max(startIndex, 0);
        const safeStop = Math.min(stopIndex, this._flattenedRenderDef.length - 1);

        for (let index = safeStart; index <= safeStop; index++) {
            const item = this._flattenedRenderDef[index];
            if (!item) {
                continue;
            }

            const element = this._rowElements[item.id];
            if (!element) {
                continue;
            }

            const height = this.measureRowHeight(element);
            if (!this.shouldUpdateRowHeight(item.id, height)) {
                continue;
            }

            this._rowHeights[item.id] = height;
            hasChanged = true;
            firstChangedIndex = Math.min(firstChangedIndex, index);
        }

        if (hasChanged && firstChangedIndex !== Number.MAX_SAFE_INTEGER) {
            this.resetVirtualizedListFromIndex(firstChangedIndex);
        }
    };

    handleItemsRendered = ({ overscanStartIndex, overscanStopIndex }: ListOnItemsRenderedProps): void => {
        this.scheduleVisibleRangeRecalculate(overscanStartIndex, overscanStopIndex);
    };

    handleListScroll = ({ scrollOffset }: ListOnScrollProps): void => {
        this._listScrollOffset = scrollOffset;

        this.scheduleSearchVisibilityRecalculate();
    };

    scheduleSearchVisibilityRecalculate = (): void => {
        if (this._searchVisibilityAnimationFrame !== null) {
            cancelAnimationFrame(this._searchVisibilityAnimationFrame);
        }

        // Keep IntersectionObserver for normal updates, but force a DOM-based sync after scroll
        // (including programmatic scrollToItem), where observer callback may not run immediately.
        this._searchVisibilityAnimationFrame = requestAnimationFrame(() => {
            this._searchVisibilityAnimationFrame = null;
            this.updateFixedSearchVisibilityFromDom();
        });
    };

    setFixedSearchVisible = (visible: boolean): void => {
        if (this._showFixedSearch === visible) {
            return;
        }

        this._showFixedSearch = visible;
        this.forceUpdate();
    };

    disposeSearchVisibilityObserver = (): void => {
        if (this._searchVisibilityObserver) {
            this._searchVisibilityObserver.disconnect();
            this._searchVisibilityObserver = null;
        }
    };

    updateFixedSearchVisibilityFromDom = (): void => {
        if (!this._virtualizedSearchElement || !this._listOuterElement) {
            this.setFixedSearchVisible(this._listScrollOffset > 0);
            return;
        }

        const searchRect = this._virtualizedSearchElement.getBoundingClientRect();
        const listRect = this._listOuterElement.getBoundingClientRect();
        const isVirtualizedSearchVisible = searchRect.bottom > listRect.top && searchRect.top < listRect.bottom;

        this.setFixedSearchVisible(!isVirtualizedSearchVisible);
    };

    setupSearchVisibilityObserver = (): void => {
        this.disposeSearchVisibilityObserver();

        if (!this._virtualizedSearchElement || !this._listOuterElement) {
            this.updateFixedSearchVisibilityFromDom();
            return;
        }

        if (typeof IntersectionObserver === "undefined") {
            this.updateFixedSearchVisibilityFromDom();
            return;
        }

        this._searchVisibilityObserver = new IntersectionObserver(([entry]) => {
            this.setFixedSearchVisible(!entry?.isIntersecting);
        }, {
            root: this._listOuterElement,
            threshold: 0
        });

        this._searchVisibilityObserver.observe(this._virtualizedSearchElement);
    };

    setVirtualizedSearchRef = (el: HTMLDivElement | null): void => {
        this._virtualizedSearchElement = el;
        this.setupSearchVisibilityObserver();
    };

    setListOuterRef = (el: HTMLDivElement | null): void => {
        this._listOuterElement = el;
        this.setupSearchVisibilityObserver();
    };

    getDefaultHeight = (type: Type): number => {
        return defaultHeightByType[type];
    };

    getItemSize = (index: number): number => {
        const item = this._flattenedRenderDef[index];
        return this._rowHeights[item?.id] ?? this.getDefaultHeight(item?.type);
    };

    isGroupCollapsed = (bc: BindingContext, def?: IFormGroupDef): ICollapsedInfo => {
        const isParentCollapsed = !!def?.parentCollections?.some(collection => {
            let parentBc = bc;
            do {
                parentBc = parentBc.getParent();
            } while (parentBc && parentBc.getNavigationPath(true) !== collection);
            return this._collapsedGroups[parentBc.getNavigationPath(false)];
        });
        const isCollapsed = isParentCollapsed || this._collapsedGroups[bc.getNavigationPath(false)];
        return { isCollapsed, isParentCollapsed };
    };

    handleGroupToggle = (bc: BindingContext): void => {
        const groupId = bc.getNavigationPath(false);
        this._collapsedGroups[groupId] = !this._collapsedGroups[groupId];
        this.resetVirtualizedListFromIndex(0);
        this.forceUpdate();
    };

    handleSearchChange = (value: string): void => {
        this._search = (value ?? "").trim();
        this._activeSearchRowId = null;
        this.forceUpdate(this.scrollToActiveSearchResult);
    };

    handleHighlightSearchResult = (value: number): void => {
        if (!this._searchMatchedRowIds.length) {
            return;
        }

        const normalizedResult = Math.max(1, Math.min(value, this._searchMatchedRowIds.length));
        this._activeSearchRowId = this._searchMatchedRowIds[normalizedResult - 1];
        this.scrollToActiveSearchResult();
        this.updateFixedSearchVisibilityFromDom();
        this.forceUpdate();
    };

    getActiveSearchResult = (): number | undefined => {
        if (!this._activeSearchRowId) {
            return undefined;
        }

        const activeResultIndex = this._searchMatchedRowIds.indexOf(this._activeSearchRowId);
        return activeResultIndex >= 0 ? activeResultIndex + 1 : undefined;
    };

    scrollToActiveSearchResult = (): void => {
        if (!this._activeSearchRowId) {
            return;
        }

        const activeResultIndex = this._flattenedRenderDef.findIndex(item => item.id === this._activeSearchRowId);
        if (activeResultIndex < 0) {
            return;
        }

        this._virtualizedListRef.current?.scrollToItem(activeResultIndex, "smart");
    };

    syncSearchResults = (flattened: IFlattenedRenderDef[]): void => {
        const matchedRowIds = flattened
            .filter(item => item.type === Type.Field && item.isSearchMatch)
            .map(item => item.id);

        this._searchMatchedRowIds = matchedRowIds;

        if (!this._search || !matchedRowIds.length) {
            this._activeSearchRowId = null;
            return;
        }

        if (!this._activeSearchRowId || !matchedRowIds.includes(this._activeSearchRowId)) {
            this._activeSearchRowId = matchedRowIds[0];
        }
    };

    setRowRef = (id: string) => (el: HTMLDivElement): void => {
        const existingObserver = this._rowResizeObservers[id];
        if (existingObserver) {
            existingObserver.disconnect();
            delete this._rowResizeObservers[id];
        }

        if (!el) {
            delete this._rowElements[id];
            return;
        }

        this._rowElements[id] = el;

        // Most rows are remeasured when they enter viewport; keep ResizeObserver only for dynamic header content.
        if (id !== "header" || typeof ResizeObserver === "undefined") {
            return;
        }

        const observer = new ResizeObserver(() => {
            const rowIndex = this._flattenedRenderDef.findIndex(item => item.id === id);

            if (rowIndex >= 0) {
                this.scheduleVisibleRangeRecalculate(rowIndex, rowIndex);
                return;
            }

            this.scheduleDomRecalculate();
        });

        observer.observe(el);
        this._rowResizeObservers[id] = observer;
    };

    prepareFlattenedGroupDef(group: IFormGroupDef, baseGroupBc: BindingContext): IFlattenedRenderDef[] {
        const { storage } = this;
        const groupId = group.id;
        const flattened: IFlattenedRenderDef[] = [];
        const _isAudit = this.isAuditTrail;

        const _getId = (type: string, bc: BindingContext, id?: string) =>
            createPath(type, bc.getNavigationPath(false), id);

        const isGroupVisible = getInfoValue(group, "isVisible", {
            storage,
            context: storage.context,
            bindingContext: baseGroupBc
        });

        // programmatically hidden group, skip rendering
        if (isGroupVisible === false) {
            return [];
        }

        // if group is collapsed, skip rendering fields, but still render group header
        const { isCollapsed, isParentCollapsed } = this.isGroupCollapsed(baseGroupBc, group);
        const forceGroupHeaderVisibility = !isParentCollapsed && !!group.collectionItemTitle;

        if (group.title && (!isCollapsed || forceGroupHeaderVisibility)) {
            flattened.push({
                id: _getId("group", baseGroupBc, groupId),
                type: Type.Group,
                bc: baseGroupBc,
                groupDef: group,
                origId: group.id
            });
        }

        if (isCollapsed) {
            return flattened;
        }

        const visibleFields: IFlattenedRenderDef[] = [];

        const _addField = (fieldBindingContext: BindingContext, origId: string, lineItemsCollectionBc?: BindingContext) => {
            const fieldInfo = storage.getInfo(fieldBindingContext);
            const isSearchMatch = !!this._search?.trim() && isFieldMatchingSearchTerm(fieldInfo, this._search);

            if (!isFieldCurrentlyVisible(storage, baseGroupBc, group, origId, fieldBindingContext)) {
                return;
            }

            visibleFields.push({
                id: _getId("field", fieldBindingContext),
                type: Type.Field,
                origId,
                bc: fieldBindingContext,
                collectionBc: lineItemsCollectionBc,
                groupDef: group,
                isSearchMatch
            });
        };

        // rows in groups
        (group.rows ?? []).forEach(row => {
            row.forEach(field => {
                const fieldBindingContext = baseGroupBc.navigate(field.id);
                _addField(fieldBindingContext, field.id);
            });
        });

        // lineItems
        let canAddNewItem = false;
        if (group.lineItems) {
            const lineItemsBc = baseGroupBc.navigate(group.lineItems.collection);

            const args: IGetValueArgs = {
                storage: this.props.storage,
                context: this.props.storage.context,
                bindingContext: lineItemsBc
            };
            canAddNewItem = _isAudit ? false : getValue<boolean>(group.lineItems.canAdd ?? true, args);

            const items = storage.getValue(lineItemsBc) as IEntity[];
            // add one row per each column per each item in the collection
            items?.forEach(item => {
                const itemBc = lineItemsBc.addKey(item);

                group.lineItems.columns?.forEach(column => {
                    const columnBc = itemBc.navigate(column.id);
                    _addField(columnBc, column.id, lineItemsBc);
                });
            });
        }
        const hasVisibleActions = _isAudit ? false : this.getVisibleGroupActions(baseGroupBc, group).length > 0;
        if (!visibleFields?.length && !hasVisibleActions && !canAddNewItem) {
            // No fields and no actions -> nothing to render
            return [];
        }

        if (!isCollapsed) {
            flattened.push(...visibleFields);

            if (canAddNewItem) {
                flattened.push({
                    id: _getId("group-add", baseGroupBc, groupId),
                    type: Type.GroupAddLineItem,
                    bc: baseGroupBc,
                    groupDef: group,
                    origId: group.id
                });
            }

            if (hasVisibleActions) {
                flattened.push({
                    id: _getId("group-footer", baseGroupBc, groupId),
                    type: Type.GroupFooter,
                    bc: baseGroupBc,
                    groupDef: group,
                    origId: group.id
                });
            }
        }

        return flattened;
    }

    /**
     * We need to preprocess variant and split group definitions by collections. Collection could be accross multiple
     * groups, so at the end, we will go through collection items and prepare copy of group for each item in the collection.
     */
    getStructuredVariant = memoizeOne((): IStructuredVariantGroupDef => {
        const { storage } = this;

        const _getDefaultStructure = (): IStructuredVariantGroupDef => ({
            groups: [],
            collections: {}
        });
        const transformedVariant = _getDefaultStructure();

        const variant = storage.getVariant();
        variant.forEach((group) => {
            const paths = group.collection ? [...splitPath(group.collection), ""] : [""];

            let currentBc = storage.data.bindingContext;
            let _lastBc = storage.data.bindingContext;
            let currentCollection = transformedVariant;
            paths.forEach(path => {
                if (!path) {
                    currentCollection.groups.push(group);
                } else {
                    currentBc = currentBc.navigate(path);
                    if (!currentBc.isCollection()) {
                        // not a collection, continue to next path segment
                        return;
                    }
                    const collectionPath = currentBc.getNavigationPath(true, _lastBc);
                    _lastBc = currentBc;
                    if (!currentCollection.collections[collectionPath]) {
                        currentCollection.collections[collectionPath] = _getDefaultStructure();
                    }
                    currentCollection = currentCollection.collections[collectionPath];
                }
            });
        });

        return transformedVariant;
    }, () => [this.storage.getVariant()]);

    getFlattenedRenderDefRec = (variant: IStructuredVariantGroupDef, baseBc: BindingContext): IFlattenedRenderDef[] => {
        const flattened: IFlattenedRenderDef[] = [];
        variant.groups.forEach(group => {
            flattened.push(...this.prepareFlattenedGroupDef(group, baseBc));
        });
        forEachKey(variant.collections, key => {
            const collectionBc = baseBc.navigate(key);
            const collectionItems = this.storage.getValue(collectionBc) as IEntity[];
            collectionItems?.forEach((item) => {
                const itemBc = collectionBc.addKey(item);
                flattened.push(...this.getFlattenedRenderDefRec(variant.collections[key], itemBc));
            });
        });
        return flattened;
    }

    getFlattenedRenderDef() {
        const { storage } = this;

        const structuredVariant = this.getStructuredVariant();
        const flattened: IFlattenedRenderDef[] = this.getFlattenedRenderDefRec(structuredVariant, storage.data.bindingContext);

        if (!this.props.hideHeader) {
            // prepend with header item
            flattened.unshift({
                id: "header",
                type: Type.Header
            });
        }

        this.syncSearchResults(flattened);
        return flattened;
    }

    handleRemoveField = (bc: BindingContext): void => {
        const { storage } = this;
        // find field definition
        const def = this._flattenedRenderDef.find(item => item.bc === bc);
        // if part of collection, remove whole collection item
        if (def.collectionBc) {
            const collectionItemBc = bc.getParentCollection();
            const items = storage.getValue(def.collectionBc) as IEntity[];
            const item = storage.getValue(collectionItemBc) as IEntity;
            const idx = items.findIndex(i => i === item);
            if (idx >= 0) {
                items.splice(idx, 1);
                storage.setValue(def.collectionBc, items);
            }
        } else {
            // otherwise, just clear data and hide the field
            storage.clearValue(bc);
            storage.addActiveFieldsFromAffectedFields(bc);
            setFieldVisibility(storage, bc, false);
        }
        this.refresh(true);
    };

    handleSetSelectedRows(selectedRows: string[], groupBc: BindingContext, def: IFormGroupDef): void {
        setMassFieldsVisibility(this.storage, groupBc, def, selectedRows);
    }

    /**
     * Search is rendered twice on the screen. Once as a part of the virtualized list (on top of the list) and when
     * the search in the list is scrolled out of view, we want to keep it at the top of the form. This is controlled by the fixedPositionSearch parameter.
     * @param fixedPositionSearch
     */
    renderSearch(fixedPositionSearch?: boolean): React.ReactElement {
        const search = (
            <VirtualizedFormSearch value={this._search}
                                   activeResult={this.getActiveSearchResult()}
                                   results={this._searchMatchedRowIds.length || undefined}
                                   inputId={fixedPositionSearch ? "virtualizedFormSearchFixed" : "virtualizedFormSearchInList"}
                                   onHighlightResult={this.handleHighlightSearchResult}
                                   onChange={this.handleSearchChange}/>
        );

        if (fixedPositionSearch) {
            return (
                <FixedSearchWrapper $visible={this._showFixedSearch}>
                    {search}
                </FixedSearchWrapper>
            );
        }

        return (
            <div ref={this.setVirtualizedSearchRef}>
                {search}
            </div>
        );
    }

    renderGroupHeader = (item: IFlattenedRenderDef): React.ReactElement => {
        const { storage } = this.props;
        const { bc, groupDef: def } = item;

        if (!def) {
            return null;
        }

        const title = getInfoValue(def, "title", { storage, bindingContext: bc });
        let collectionTitle: string;
        let isCollapsed: boolean;
        if (def.collectionItemTitle) {
            // get collection item title and expanded flag, render another "main" group, which will be
            // expanding / collapsing the other groups in the collection
            const itemsBc = bc.removeKey();
            const allItems = storage.getValue(itemsBc) as IEntity[];
            const item = storage.getValue(bc);
            const idx = allItems.findIndex(i => i === item);
            collectionTitle = getGroupCollectionTitle(storage, def, idx, item);
            isCollapsed = this.isGroupCollapsed(bc).isCollapsed;
        }

        const isSubGroup = !!def.parentCollections?.length;
        return (
            <GroupHeaderWrapper $isSubGroup={collectionTitle && isSubGroup}>
                {collectionTitle && (
                    <GroupHeaderStyled title={collectionTitle}
                                       showLine={!isSubGroup}>
                        <IconButtonStyledToggle
                            title={this.props.t(`Components:CollapsibleSection.${isCollapsed ? "Show" : "Hide"}`)}
                            isDecorative
                            onClick={this.handleGroupToggle.bind(this, bc)}
                            _isToggled={!isCollapsed}>
                            <CaretIcon width={IconSize.M} height={IconSize.M}/>
                        </IconButtonStyledToggle>
                    </GroupHeaderStyled>
                )}
                {!isCollapsed && (
                    <GroupHeaderStyled title={title}
                               tooltip={getTooltip(def.tooltip, storage)}
                               showLine={def.showGroupDivider !== false}>
                        {/*this.props.headerContent*/}
                    </GroupHeaderStyled>
                )}
            </GroupHeaderWrapper>
        );
    };

    getVisibleGroupActions(groupBc: BindingContext, def: IFormGroupDef): IActionItem[] {
        let actions: IActionItem[] = [...(def.actions ?? [])];
        const { storage } = this;
        actions = actions.filter(action => {
            const { isDisabled, ...info } = action;
            return isVisible({ info, storage, bindingContext: groupBc, context: this.props.storage.context });
        });

        if (!storage.isReadOnly && getAddableFields(storage, groupBc, def).length > 0) {
            actions.unshift({
                id: CUSTOMIZE_FIELDS_ACTION,
                title: i18next.t("Common:General.Add"),
                isTransparent: false
            });
        }

        return actions;
    }

    wrapVirtualizedRow = (row: React.ReactElement, item: IFlattenedRenderDef, style: React.CSSProperties, isColumn?: boolean): React.ReactElement => {
        return (
            <div style={style}>
                <div id={item.id} data-testid={TestIds.VirtualizedFormRow}
                     ref={this.setRowRef(item.id)}
                     style={{
                         display: "flex",
                         alignItems: isColumn ? "flex-start" : "center",
                         flexDirection: isColumn ? "column" : "row",
                         minHeight: this.getDefaultHeight(item.type),
                         padding: `0 ${VIEW_PADDING_VALUE}px` }}>
                    {row}
                </div>
            </div>
        );
    };

    renderVirtualizedRow = ({ index, style, data }: ListChildComponentProps<IListItemData>): React.ReactElement => {
        const item = data.flattened[index];

        if (!item) {
            return null;
        }

        const _isAudit = this.isAuditTrail;
        let isColumn = false;
        let component: React.ReactElement | null = null;

        switch (item.type) {
            case Type.Group:
                const groupId = item.origId;
                if (groupId) {
                    component = this.renderGroupHeader(item);
                }
                break;
            case Type.GroupAddLineItem:
                component = (
                    <VirtualizedFormLineItemsAdd storage={this.storage}
                                                 def={item.groupDef}
                                                 bindingContext={item.bc}
                                                 onAction={this.props.onLineItemAction} />
                );
                break;
            case Type.GroupFooter:
                component = (
                    <VirtualizedFormGroupFooter storage={this.storage}
                                                def={item.groupDef}
                                                bindingContext={item.bc}
                                                actions={this.getVisibleGroupActions(item.bc, item.groupDef)}
                                                onSelectRows={this.handleSetSelectedRows}
                                                onGroupAction={null}/>
                );
                break;
            case Type.Header:
                isColumn = true;
                component = (<>
                    {this.renderHeader()}
                    {_isAudit ? null : this.renderInfoAlert()}
                    {_isAudit ? null : this.renderAlertAtPosition(FormAlertPosition.Top)}
                    {_isAudit ? null : this.renderSearch()}
                </>);
                break;
            case Type.Field:
                const { bc, collectionBc, groupDef } = item;
                const fieldDef = this.getFieldDefinition(bc);
                if (!fieldDef) {
                    return null;
                }
                let isCollectionItemRemovable = true;
                if (collectionBc && groupDef.lineItems?.isTheOnlyItemRemovable === false) {
                    isCollectionItemRemovable = this.storage.getValue(collectionBc)?.length > 1;
                }
                component = (
                    <SmartListField key={bc.toString()}
                                    bindingContext={bc}
                                    style={fieldDef?.customFieldStyle}
                                    storage={this.props.storage}
                                    onConfirm={this.handleConfirm}
                                    onCancel={this.handleCancel}
                                    onFieldStateChange={this.handleFieldStateChange}
                                    onTemporalChange={this.handleTemporalChange}
                                    onChange={this.handleChange}
                                    onBlur={this.handleBlur}
                                    onRemoveField={this.handleRemoveField}
                                    canRemove={collectionBc ? isCollectionItemRemovable : undefined}
                                    hasMatch={!!item.isSearchMatch}
                                    highlightTerm={this._search}
                                    isSearchResultActive={item.id === this._activeSearchRowId}
                                    fieldDef={fieldDef}
                    />
                );
                break;
            case Type.Custom:
                component = (<>
                    {item.type === Type.Custom ? `custom-${item.origId}` : item.origId}
                </>);
                break;
        }

        return this.wrapVirtualizedRow(component, item, style, isColumn);
    };

    renderContent = (): React.ReactElement => {
        const formDef = this.props.storage.data.definition;
        const isFormReadOnly = getInfoValue(formDef, "isReadOnly", {
            storage: this.props.storage,
            data: this.props.storage.data.entity
        });
        const formElement: React.ElementType = isFormReadOnly ? "div" : "form";
        const _isAudit = this.isAuditTrail;

        this._flattenedRenderDef = this.getFlattenedRenderDef();

        const formContentArgs = isFormReadOnly ? {} : {
            // novalidate prevents browser validation overlays in editable forms
            noValidate: true
        };

        const itemData: IListItemData = {
            flattened: this._flattenedRenderDef,
            search: this._search,
            activeSearchRowId: this._activeSearchRowId,
            searchMatchedRowIds: this._searchMatchedRowIds
        };

        return (
            <ViewStyled hotspotContextId={this.props.storage.id}
                        $useHorizontalScrollbar={this.props.useHorizontalScrollbar}
                        renderScrollbar={false}
                        withoutPadding
                        withoutViewWrapperPadding={false /* keep top padding on the wrapper, so there is space for breadcrumbs */}
                        floatingAlert={this.renderAlertAtPosition(FormAlertPosition.Bottom)}
                        fixedFooter={this.props.children && (
                            <FormFooter hasTopMargin={!isFormReadOnly}
                                        isEditOverride={!!this.props.editOverride}
                                        $addViewHorizontalPadding>
                                {this.props.children}
                            </FormFooter>
                        )}
                        passRef={this.props.passRef}
                        style={this.props.style}
                        className={this.props.className}
                        testid={TestIds.FormView}>
                {!_isAudit && this.renderSearch(true)}
                <FormContent as={formElement}
                             onSubmit={this.handleFormSubmit}
                             isEditOverride={!!this.props.editOverride}
                             ref={composeRefHandlers(this.props.refContent, this._refFormContent)}
                             withoutPadding
                             buttonsOnBottom
                             style={{ display: "flex", flexDirection: "column", minHeight: "100%" }}
                             data-testid={TestIds.FormContent}
                             {...formContentArgs}>
                    <AutoSizer defaultWidth={0} defaultHeight={0}>
                        {({ width, height }: { width: number, height: number }) => {
                            if (width === 0 || height === 0) {
                                // we wait on measured width and height, otherwise the list will not work well
                                return null;
                            }
                            return (
                                <VariableSizeListStyledTyped
                                    width={width}
                                    height={height}
                                    itemCount={this._flattenedRenderDef.length}
                                    itemSize={this.getItemSize}
                                    overscanCount={5}
                                    onItemsRendered={this.handleItemsRendered}
                                    itemData={itemData}
                                    onScroll={this.handleListScroll}
                                    outerRef={composeRefHandlers(this.setListOuterRef, this.props.refScroll)}
                                    ref={this._virtualizedListRef}
                                    data-testid={TestIds.VirtualizedFormList}>
                                    {this.renderVirtualizedRow}
                                </VariableSizeListStyledTyped>
                            );
                        }}
                    </AutoSizer>
                </FormContent>
            </ViewStyled>
        );
    };
}

export default withPermissionContext(withOData(withTranslation([...FormTranslationFiles])(VirtualizedForm)));
